import type { ContentItemId, WatchedView } from '@kidshub/contracts';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { formatClock } from '../lib/duration.js';
import { toErrorCode, toErrorKey } from '../lib/error-code.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { ButtonLink, Card, EmptyState, Icon, type IconName } from '../ui/kit.js';
import { CoverageChart, ViewTrack } from './charts.js';
import { coverage, isWalkAway, seeksOf, watchedSeconds } from './coverage.js';
import { PeriodSwitch, useDurationText, useWatchingScope } from './watching-scope.js';
import './watching.css';

/**
 * One video, through one child's viewings of it.
 *
 * Which moments she watched again, where she walked away, and each opening as
 * a strip a parent can read at a glance. All of it comes from the views a
 * tablet reports; before a tablet does, this says so and shows the counts the
 * sessions already give.
 */

/** How a viewing's ending reads, and the icon and tone that carry it. */
const endingLook = (
  view: WatchedView,
): { tone: 'done' | 'left' | 'stop'; icon: IconName; key: string } | null => {
  const end = view.end;
  if (end === null) {
    return null;
  }
  switch (end.reason) {
    case 'COMPLETED':
      return { tone: 'done', icon: 'check', key: 'watching.ending.COMPLETED' };
    case 'LEFT':
    case 'BACKGROUND':
      return { tone: 'left', icon: 'x', key: 'watching.ending.LEFT' };
    case 'STOPPED':
      if (end.gateReason === 'PARENT_PAUSED') {
        return { tone: 'stop', icon: 'pause', key: 'watching.ending.PARENT' };
      }
      if (end.gateReason === 'REST_PERIOD' || end.gateReason === 'MOVEMENT_BREAK') {
        return { tone: 'stop', icon: 'pause', key: 'watching.ending.BREAK' };
      }
      return { tone: 'stop', icon: 'hourglass', key: 'watching.ending.TIME_UP' };
    case 'UNAVAILABLE':
      return { tone: 'stop', icon: 'alert', key: 'watching.ending.UNAVAILABLE' };
  }
};

export const WatchingItemPage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const duration = useDurationText();
  const { contentItemId } = useParams();

  const children = useResource('children', () => apiClient.children.list());
  const content = useResource('content', () => apiClient.catalog.list());
  const collections = useResource('collections', () => apiClient.collections.list());

  const familyChildren =
    children.resource.status === 'ready' ? children.resource.data.children : [];
  const scope = useWatchingScope(familyChildren);
  const childId = scope.child?.child.id ?? null;

  const detail = useResource(`watching:${childId}:${contentItemId}:${scope.days}`, () =>
    childId === null || contentItemId === undefined
      ? Promise.resolve(null)
      : apiClient.analytics.item({
          childId,
          contentItemId: contentItemId as ContentItemId,
          days: scope.days,
        }),
  );

  const item =
    content.resource.status === 'ready'
      ? content.resource.data.items.find((entry) => entry.id === contentItemId)
      : undefined;
  const home =
    collections.resource.status === 'ready'
      ? collections.resource.data.collections.find((collection) =>
          collection.itemIds.some((id) => id === contentItemId),
        )
      : undefined;
  const data = detail.resource.status === 'ready' ? detail.resource.data : null;
  const length = item?.durationSeconds ?? null;
  const name = scope.child?.child.name ?? '';
  const when = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  const views = data?.views ?? [];
  const found = length === null ? null : coverage(views, length);
  const completed = views.filter((view) => view.end?.reason === 'COMPLETED').length;

  return (
    <AppShell
      title={item?.title ?? t('common.loading')}
      subtitle={[
        home?.title,
        length === null ? null : formatClock(length),
        t(`watching.periodText.${scope.days}`),
      ]
        .filter(Boolean)
        .join(' · ')}
      actions={
        <>
          <PeriodSwitch days={scope.days} onChange={scope.setDays} />
          {home === undefined ? null : (
            <ButtonLink variant="secondary" to={`/library/collections/${home.id}`}>
              {t('watching.item.openCollection')}
            </ButtonLink>
          )}
        </>
      }
    >
      <ButtonLink
        variant="ghost"
        small
        icon="back"
        to={`/watching${scope.search}`}
        className="cp-back"
      >
        {t('watching.title')}
      </ButtonLink>

      {detail.resource.status === 'failed' ? (
        toErrorCode(detail.resource.error) === 'CONTENT_NOT_FOUND' ? (
          <EmptyState title={t('watching.item.notFound')} />
        ) : (
          <p className="help err" role="alert">
            {t(toErrorKey(detail.resource.error))}
          </p>
        )
      ) : null}

      {data === null ? null : (
        <>
          <div className="wa-kpis">
            <div className="wa-kpi">
              <b>{data.opens}</b>
              <span>{t('watching.item.opens')}</span>
              <small>{name}</small>
            </div>
            <div className="wa-kpi">
              <b>{duration(data.watchedSeconds)}</b>
              <span>{t('watching.item.watched')}</span>
              <small>
                {data.opens === 0
                  ? '—'
                  : t('watching.item.average', {
                      duration: formatClock(Math.round(data.watchedSeconds / data.opens)),
                    })}
              </small>
            </div>
            <div className="wa-kpi">
              <b>{views.length === 0 ? '—' : completed}</b>
              <span>{t('watching.item.completed')}</span>
              <small>
                {views.length === 0
                  ? t('watching.pendingShort')
                  : t('watching.item.ofViews', { count: views.length })}
              </small>
            </div>
            <div className="wa-kpi">
              <b>{formatClock(data.resumeSeconds)}</b>
              <span>{t('watching.item.resume')}</span>
              <small>{t('watching.item.resumeDetail')}</small>
            </div>
          </div>

          <Card title={t('watching.item.coverageTitle')} meta={t('watching.item.coverageMeta')}>
            {found === null || length === null || views.length === 0 ? (
              <p className="wa-pending">
                {data.hasViews ? t('watching.item.noViews') : t('watching.pending')}
              </p>
            ) : (
              <>
                <CoverageChart
                  data={found}
                  durationSeconds={length}
                  label={t('watching.item.coverageTitle')}
                  tipFor={(seconds, count, exits) => (
                    <>
                      <b>{formatClock(seconds)}</b>
                      {t('watching.item.coverageTip', { count })}
                      {exits > 0 ? (
                        <>
                          <br />
                          {t('watching.item.exitTip', { count: exits })}
                        </>
                      ) : null}
                    </>
                  )}
                />
                <div className="wa-legend">
                  <span>
                    <i className="wa-key-line" />
                    {t('watching.item.legendCount')}
                  </span>
                  <span>
                    <i className="wa-key-band" />
                    {t('watching.item.legendAgain')}
                  </span>
                  <span>
                    <i className="wa-key-exit" />
                    {t('watching.item.legendLeft')}
                  </span>
                </div>
                <div className="wa-callouts">
                  <div className="wa-callout">
                    <b>
                      {found.rewatched === null
                        ? '—'
                        : `${formatClock(found.rewatched.from)}–${formatClock(found.rewatched.to)}`}
                    </b>
                    <span>
                      {found.rewatched === null
                        ? t('watching.item.noRewatch')
                        : t('watching.item.rewatched', { count: found.rewatched.times })}
                    </span>
                  </div>
                  <div className="wa-callout">
                    <b>{found.leftMost === null ? '—' : formatClock(found.leftMost.at)}</b>
                    <span>
                      {found.leftMost === null
                        ? t('watching.item.neverLeft')
                        : t('watching.item.leftMost', { count: found.leftMost.times })}
                    </span>
                  </div>
                  <div className="wa-callout">
                    <b>
                      {completed} / {views.length}
                    </b>
                    <span>{t('watching.item.completedCallout')}</span>
                  </div>
                </div>
              </>
            )}
          </Card>

          <Card title={t('watching.item.viewsTitle')} meta={t('watching.item.viewsMeta')}>
            {views.length === 0 || length === null ? (
              <p className="wa-pending">
                {data.hasViews ? t('watching.item.noViews') : t('watching.pending')}
              </p>
            ) : (
              <>
                <ol className="wa-views">
                  {views.map((view) => {
                    const look = endingLook(view);
                    const last = view.ranges[view.ranges.length - 1];
                    const seeks = seeksOf(view.ranges);
                    return (
                      <li key={view.viewId} className="wa-view">
                        <div className="wa-view-head">
                          <b>{when.format(new Date(view.openedAt))}</b>
                          <span>
                            {t('watching.item.watchedFor', {
                              duration: formatClock(Math.round(watchedSeconds(view.ranges))),
                            })}
                          </span>
                          {look === null ? (
                            <span>{t('watching.item.open')}</span>
                          ) : (
                            <span className={`wa-end ${look.tone}`}>
                              <Icon name={look.icon} small />
                              {t(look.key)}
                              {look.tone === 'done' || last === undefined
                                ? ''
                                : ` · ${formatClock(Math.round(last[1]))}`}
                            </span>
                          )}
                        </div>
                        <ViewTrack
                          ranges={view.ranges}
                          durationSeconds={length}
                          exitAt={isWalkAway(view) && last !== undefined ? last[1] : null}
                        />
                        {seeks.length === 0 ? null : (
                          <small>
                            {seeks
                              .map((seek) =>
                                t(
                                  seek.to > seek.from
                                    ? 'watching.item.seekForward'
                                    : 'watching.item.seekBack',
                                  {
                                    from: formatClock(Math.round(seek.from)),
                                    to: formatClock(Math.round(seek.to)),
                                  },
                                ),
                              )
                              .join(' · ')}
                          </small>
                        )}
                      </li>
                    );
                  })}
                </ol>
                <div className="wa-legend">
                  <span>
                    <i className="wa-key-seg" />
                    {t('watching.item.legendWatched')}
                  </span>
                  <span>
                    <i className="wa-key-again" />
                    {t('watching.item.legendAgain')}
                  </span>
                  <span>
                    <i className="wa-key-exit" />
                    {t('watching.item.legendLeft')}
                  </span>
                </div>
              </>
            )}
          </Card>
        </>
      )}
    </AppShell>
  );
};
